import { useState, useEffect, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Bell, CheckCheck, AlertTriangle, TrendingUp, Zap, Globe, ChevronLeft, ChevronRight, ArrowUpRight, ArrowDownRight, Search, ExternalLink } from "lucide-react";
import { fetchAlerts, markAlertRead, markAllAlertsRead, fetchAlertConfig, updateAlertConfig, fetchContentOpportunities, TAG_NAMES, TAG_COLORS, SOCIAL_CATEGORIES, SOCIAL_CAT_COLORS } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

const ALERT_ICONS = {
  mention_spike: Zap,
  breakout_query: AlertTriangle,
  rising_query: TrendingUp,
  new_domain: Globe,
};

const ALERT_COLORS = {
  mention_spike: 'text-yellow-400',
  breakout_query: 'text-red-400',
  rising_query: 'text-emerald-400',
  new_domain: 'text-blue-400',
};

export default function AlertsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const activeCategory = searchParams.get("category") || "all";

  const [alerts, setAlerts] = useState([]);
  const [categorySummary, setCategorySummary] = useState([]);
  const [unread, setUnread] = useState(0);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [config, setConfig] = useState({ mention_spike_threshold: 20, rising_query_threshold: 100 });
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const params = { page, limit: 50 };
      if (activeCategory !== "all") params.category = activeCategory;

      const [alertData, configData] = await Promise.all([
        fetchAlerts(params),
        fetchAlertConfig()
      ]);
      setAlerts(alertData.alerts || []);
      setUnread(alertData.unread || 0);
      setTotal(alertData.total || 0);
      setPages(alertData.pages || 1);
      setCategorySummary(alertData.category_summary || []);
      setConfig(configData);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [activeCategory, page]);

  useEffect(() => { setLoading(true); load(); }, [load]);

  const handleCategoryFilter = (cat) => {
    setPage(1);
    if (cat === "all") {
      setSearchParams({});
    } else {
      setSearchParams({ category: cat });
    }
  };

  const handleMarkRead = async (id) => {
    await markAlertRead(id);
    load();
  };

  const handleMarkAll = async () => {
    await markAllAlertsRead();
    load();
    toast.success("All alerts marked as read");
  };

  const handleSaveConfig = async () => {
    try {
      await updateAlertConfig(config);
      toast.success("Alert thresholds updated");
    } catch (e) {
      toast.error("Failed to update config");
    }
  };

  if (loading) {
    return <div className="space-y-4" data-testid="alerts-loading">
      {[...Array(4)].map((_, i) => <div key={i} className="h-16 bg-gray-100 animate-pulse rounded-none" />)}
    </div>;
  }

  return (
    <div className="space-y-6" data-testid="alerts-page">
      {/* Config */}
      <Card className="glass-card border-gray-200 rounded-none" data-testid="alert-config">        <CardHeader className="pb-2 border-b border-gray-200">
          <CardTitle className="font-display text-sm font-semibold tracking-wider uppercase text-gray-800">
            Alert Thresholds
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label className="font-display text-xs uppercase tracking-wider text-gray-600">
                Mention Spike (per hour)
              </Label>
              <Input
                type="number"
                value={config.mention_spike_threshold}
                onChange={(e) => setConfig(c => ({ ...c, mention_spike_threshold: parseInt(e.target.value) || 0 }))}
                className="rounded-none bg-white border-gray-300 font-mono-data h-9"
                data-testid="mention-threshold-input"
              />
            </div>
            <div className="space-y-2">
              <Label className="font-display text-xs uppercase tracking-wider text-gray-600">
                Rising Query (% increase)
              </Label>
              <Input
                type="number"
                value={config.rising_query_threshold}
                onChange={(e) => setConfig(c => ({ ...c, rising_query_threshold: parseInt(e.target.value) || 0 }))}
                className="rounded-none bg-white border-gray-300 font-mono-data h-9"
                data-testid="rising-threshold-input"
              />
            </div>
          </div>
          <Button onClick={handleSaveConfig} className="mt-4 rounded-none bg-[#FFEE2F] text-[#1E191A] hover:bg-[#E6D800] font-display text-xs font-bold tracking-widest uppercase h-9 px-5" data-testid="save-config-btn">
            SAVE THRESHOLDS
          </Button>
        </CardContent>
      </Card>

      {/* Content Opportunities */}
      <ContentOpportunitiesSection />

      {/* Category Filter Tabs */}
      <Card className="glass-card border-gray-200 rounded-none" data-testid="alert-category-tabs">
        <CardHeader className="pb-2 border-b border-gray-200">
          <CardTitle className="font-display text-sm font-semibold tracking-wider uppercase text-gray-800">
            Alerts by Category
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-4">
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => handleCategoryFilter("all")}
              className={`px-3 py-1.5 text-xs font-display uppercase tracking-wider font-semibold transition-all duration-200 border ${
                activeCategory === "all"
                  ? "bg-gray-100 border-gray-300 text-white"
                  : "bg-transparent border-gray-200 text-gray-500 hover:text-gray-800 hover:border-gray-200"
              }`}
              data-testid="alert-filter-all"
            >
              ALL ({categorySummary.reduce((s, c) => s + c.count, 0)})
            </button>
            {categorySummary.map((cat) => (
              <button
                key={cat.tag}
                onClick={() => handleCategoryFilter(cat.tag)}
                className={`px-3 py-1.5 text-xs font-display uppercase tracking-wider font-semibold transition-all duration-200 border flex items-center gap-2 ${
                  activeCategory === cat.tag
                    ? "border-opacity-40 text-white"
                    : "bg-transparent border-gray-200 text-gray-500 hover:text-gray-800 hover:border-gray-200"
                }`}
                style={activeCategory === cat.tag ? {
                  background: `${TAG_COLORS[cat.tag] || '#6B7280'}15`,
                  borderColor: `${TAG_COLORS[cat.tag] || '#6B7280'}40`,
                  color: TAG_COLORS[cat.tag] || '#6B7280'
                } : {}}
                data-testid={`alert-filter-${cat.tag}`}
              >
                <span className="w-2 h-2 rounded-sm" style={{ background: TAG_COLORS[cat.tag] || '#6B7280' }} />
                {cat.name}
                <span className="font-mono-data text-[10px] opacity-70">
                  {cat.count}
                  {cat.unread > 0 && <span className="text-red-400 ml-1">({cat.unread})</span>}
                </span>
              </button>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Alert History */}
      <Card className="glass-card border-gray-200 rounded-none" data-testid="alert-history">
        <CardHeader className="pb-2 border-b border-gray-200 flex flex-row items-center justify-between">
          <CardTitle className="font-display text-sm font-semibold tracking-wider uppercase text-gray-800">
            {activeCategory === "all" ? "All Alerts" : `${TAG_NAMES[activeCategory] || activeCategory} Alerts`}
            {" "}({total})
            {unread > 0 && <Badge className="ml-2 bg-red-500/20 text-red-400 rounded-sm text-[9px] font-mono-data">{unread} UNREAD</Badge>}
          </CardTitle>
          {unread > 0 && (
            <Button variant="ghost" size="sm" onClick={handleMarkAll} className="rounded-none text-xs font-display uppercase tracking-wider" data-testid="mark-all-read-btn">
              <CheckCheck className="w-3 h-3 mr-1.5" /> MARK ALL READ
            </Button>
          )}
        </CardHeader>
        <CardContent className="pt-0">
          {alerts.length === 0 ? (
            <div className="py-16 text-center">
              <Bell className="w-8 h-8 text-gray-400 mx-auto mb-3" />
              <p className="font-mono-data text-sm text-gray-400">No alerts {activeCategory !== "all" ? `for ${TAG_NAMES[activeCategory] || activeCategory}` : "yet"}</p>
            </div>
          ) : (
            alerts.map((alert, i) => {
              const Icon = ALERT_ICONS[alert.type] || Bell;
              const colorClass = ALERT_COLORS[alert.type] || 'text-gray-600';
              return (
                <div
                  key={alert.id || i}
                  className={`feed-item flex items-start gap-3 ${!alert.read ? 'bg-gray-50' : ''}`}
                  data-testid={`alert-item-${i}`}
                >
                  <Icon className={`w-4 h-4 mt-0.5 flex-shrink-0 ${colorClass}`} />
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm ${alert.read ? 'text-gray-500' : 'text-gray-800'}`}>{alert.message}</p>
                    <div className="flex items-center gap-3 mt-1.5 flex-wrap">
                      <span className="font-mono-data text-[10px] text-gray-400">
                        {alert.timestamp ? new Date(alert.timestamp).toLocaleString('en-AU') : ''}
                      </span>
                      <Badge className="rounded-sm text-[9px] font-mono-data bg-gray-100 text-gray-500 border-none">
                        {alert.type?.replace('_', ' ').toUpperCase()}
                      </Badge>
                      {alert.category && (
                        <button
                          onClick={() => handleCategoryFilter(alert.category)}
                          className="inline-flex items-center gap-1 rounded-sm text-[9px] font-mono-data px-1.5 py-0 transition-colors duration-200 hover:opacity-80 cursor-pointer"
                          style={{
                            background: `${TAG_COLORS[alert.category] || '#6B7280'}15`,
                            color: TAG_COLORS[alert.category] || '#6B7280',
                            border: `1px solid ${TAG_COLORS[alert.category] || '#6B7280'}30`
                          }}
                          data-testid={`alert-cat-badge-${i}`}
                        >
                          <span className="w-1.5 h-1.5 rounded-sm" style={{ background: TAG_COLORS[alert.category] }} />
                          {TAG_NAMES[alert.category] || alert.category}
                        </button>
                      )}
                      {alert.term && (
                        <span className="font-mono-data text-[10px] text-gray-400 italic">"{alert.term}"</span>
                      )}
                    </div>
                  </div>
                  {!alert.read && (
                    <Button variant="ghost" size="sm" onClick={() => handleMarkRead(alert.id)} className="rounded-none text-xs text-gray-500 hover:text-white" data-testid={`mark-read-${i}`}>
                      <CheckCheck className="w-3 h-3" />
                    </Button>
                  )}
                </div>
              );
            })
          )}
          {/* Pagination */}
          {pages > 1 && (
            <div className="flex items-center justify-center gap-4 pt-4 border-t border-gray-200">
              <Button variant="ghost" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)} className="rounded-none" data-testid="alerts-prev">
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <span className="font-mono-data text-xs text-gray-500">{page} / {pages}</span>
              <Button variant="ghost" size="sm" disabled={page >= pages} onClick={() => setPage(p => p + 1)} className="rounded-none" data-testid="alerts-next">
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function ContentOpportunitiesSection() {
  const [opportunities, setOpportunities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterCat, setFilterCat] = useState('all');
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const data = await fetchContentOpportunities({ trend_filter: showAll ? 'all' : 'upward' });
        setOpportunities(data.opportunities || []);
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    })();
  }, [showAll]);

  const filtered = filterCat === 'all' ? opportunities : opportunities.filter(o => o.category === filterCat || o.category_name?.toLowerCase() === filterCat);
  const gaps = filtered.filter(o => o.jb_page_status === 'gap').length;
  const existing = filtered.filter(o => o.jb_page_status === 'existing').length;

  return (
    <Card className="glass-card border-gray-200 rounded-none" data-testid="content-opportunities">
      <CardHeader className="pb-3 border-b border-gray-200">
        <CardTitle className="font-display text-sm font-bold tracking-wider uppercase text-gray-800 flex items-center gap-2">
          <Search className="w-4 h-4 text-[#FFEE2F]" />
          Content Opportunities
          <Badge variant="outline" className="text-[10px] border-orange-500/30 text-orange-400 ml-2">{gaps} gaps</Badge>
          <Badge variant="outline" className="text-[10px] border-gray-300 text-gray-500">{existing} covered</Badge>
          <button
            onClick={() => setShowAll(!showAll)}
            className={`ml-auto px-3 py-1 text-[10px] font-display uppercase tracking-wider border transition-colors ${
              showAll ? 'border-gray-300 text-gray-600 bg-gray-100' : 'border-emerald-500/30 text-emerald-400 bg-emerald-500/10'
            }`}
            data-testid="trend-filter-toggle"
          >
            {showAll ? 'Showing All' : 'Upward Trends Only'}
          </button>
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-4 space-y-4">
        {/* Category filter */}
        <div className="flex flex-wrap gap-2">
          <button onClick={() => setFilterCat('all')}
            className={`px-3 py-1.5 text-xs font-display uppercase tracking-wider font-semibold border transition-colors ${
              filterCat === 'all' ? 'bg-yellow-400/10 border-yellow-400/40 text-yellow-400' : 'border-gray-200 text-gray-500 hover:text-gray-800'
            }`} data-testid="opp-cat-all">All</button>
          {Object.entries(SOCIAL_CATEGORIES).map(([key, name]) => (
            <button key={key} onClick={() => setFilterCat(key)}
              className={`px-3 py-1.5 text-xs font-display uppercase tracking-wider font-semibold border transition-colors ${
                filterCat === key ? 'text-white' : 'border-gray-200 text-gray-500 hover:text-gray-800'
              }`}
              style={filterCat === key ? { background: `${SOCIAL_CAT_COLORS[key]}15`, borderColor: `${SOCIAL_CAT_COLORS[key]}40`, color: SOCIAL_CAT_COLORS[key] } : {}}
              data-testid={`opp-cat-${key}`}>
              {name}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="space-y-2">{[...Array(5)].map((_, i) => <div key={i} className="h-8 bg-gray-100 animate-pulse" />)}</div>
        ) : filtered.length === 0 ? (
          <div className="py-8 text-center">
            <Search className="w-8 h-8 text-gray-400 mx-auto mb-3" />
            <p className="font-mono-data text-sm text-gray-400">No content opportunities {showAll ? 'found yet' : 'with upward trends'}</p>
            <p className="font-mono-data text-xs text-gray-400 mt-1">
              {showAll ? 'Opportunities are computed daily from trending social themes with search volume' : 'Try "Showing All" to see opportunities without trend filter'}
            </p>
          </div>
        ) : (
          <div className="space-y-0">
            <div className="flex items-center py-2 border-b border-gray-200">
              <div className="flex-1 font-display text-[10px] text-gray-400 uppercase tracking-widest">Keyword / Theme</div>
              <div className="w-20 text-right font-display text-[10px] text-gray-400 uppercase tracking-widest">Search Vol</div>
              <div className="w-20 text-right font-display text-[10px] text-gray-400 uppercase tracking-widest">3M Trend</div>
              <div className="w-20 text-right font-display text-[10px] text-gray-400 uppercase tracking-widest">YoY</div>
              <div className="w-28 text-center font-display text-[10px] text-gray-400 uppercase tracking-widest">JB Status</div>
              <div className="w-20 text-center font-display text-[10px] text-gray-400 uppercase tracking-widest">Category</div>
            </div>
            {filtered.map((opp, i) => {
              const tmc = opp.three_month_change;
              const yoy = opp.yoy_change;
              return (
                <div key={opp.term + '-' + i} className="flex items-center py-2.5 border-b border-gray-100 hover:bg-gray-50 transition-colors" data-testid={`opp-row-${i}`}>
                  <div className="flex-1 font-mono-data text-xs text-gray-800 flex items-center gap-2">
                    {opp.term}
                    {opp.jb_page_url && (
                      <a href={opp.jb_page_url} target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-gray-600">
                        <ExternalLink className="w-3 h-3" />
                      </a>
                    )}
                  </div>
                  <div className="w-20 text-right font-mono-data text-xs font-bold text-yellow-400">{opp.search_volume?.toLocaleString() || '-'}</div>
                  <div className="w-20 text-right font-mono-data text-xs">
                    {tmc != null ? (
                      <span className={tmc > 0 ? 'text-emerald-400' : tmc < 0 ? 'text-red-400' : 'text-gray-500'}>
                        {tmc > 0 ? <ArrowUpRight className="w-3 h-3 inline" /> : tmc < 0 ? <ArrowDownRight className="w-3 h-3 inline" /> : null}
                        {tmc > 0 ? '+' : ''}{tmc}%
                      </span>
                    ) : <span className="text-gray-400 text-[10px]">NEW</span>}
                  </div>
                  <div className="w-20 text-right font-mono-data text-xs">
                    {yoy != null ? (
                      <span className={yoy > 0 ? 'text-emerald-400' : yoy < 0 ? 'text-red-400' : 'text-gray-500'}>
                        {yoy > 0 ? '+' : ''}{yoy}%
                      </span>
                    ) : <span className="text-gray-400 text-[10px]">NEW</span>}
                  </div>
                  <div className="w-28 text-center">
                    {opp.jb_page_status === 'gap' ? (
                      <Badge className="text-[10px] bg-orange-500/15 text-orange-400 border border-orange-500/30">Gap - No Page</Badge>
                    ) : (
                      <Badge className="text-[10px] bg-gray-200 text-gray-500 border border-gray-300/30">Existing Page</Badge>
                    )}
                  </div>
                  <div className="w-20 text-center">
                    <span className="font-mono-data text-[10px] px-1.5 py-0.5 rounded" style={{
                      background: `${SOCIAL_CAT_COLORS[opp.category] || '#6B7280'}15`,
                      color: SOCIAL_CAT_COLORS[opp.category] || '#6B7280',
                    }}>{opp.category_name || opp.category}</span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
